<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">Vídeos</h1>
          <div class="appui-crumb">App <span class="sep">/</span> Vídeos</div>
        </div>
      </div>

      <!-- Vazio: nenhum cadastrado -->
      <div v-if="!list.length" class="appui-emptystate">
        <div class="appui-emptystate-icon"><i class="bx bx-video"></i></div>
        <div class="appui-emptystate-title">Nenhum vídeo ainda</div>
        <div class="appui-emptystate-sub">Adicione vídeos do YouTube ou Vimeo para o participante assistir dentro do app.</div>
        <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar o primeiro vídeo</button>
      </div>

      <!-- Bloco principal -->
      <section v-else class="appui-card appui-card--flush">
        <div class="appui-card-toolbar">
          <div class="appui-card-toolbar-left">
            <div class="appui-search">
              <i class="bx bx-search-alt"></i>
              <input v-model.trim="search" placeholder="Buscar por título...">
              <button v-if="search" class="appui-search-clear" @click="search = ''" title="Limpar busca"><i class="mdi mdi-close"></i></button>
            </div>
          </div>
          <div class="appui-card-toolbar-right">
            <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Novo vídeo</button>
          </div>
        </div>

        <!-- Sem resultado de busca -->
        <div v-if="!filteredList.length" class="appui-inline-empty">
          Nenhum vídeo encontrado para os filtros. <a @click="clearFilters()">Limpar filtros</a>
        </div>

        <!-- Grade (reordenação por arrastar) -->
        <draggable
          v-else
          v-model="displayList"
          :element="'div'"
          class="appui-grid-3"
          style="padding:16px"
          :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost', disabled: isFiltering }"
          @end="onReorder"
        >
          <div v-for="v in displayList" :key="v.id" class="appui-vid-card">
            <div class="appui-video-thumb">
              <img v-if="v.url_image" :src="v.url_image">
              <span v-else class="play"><i class="mdi mdi-play"></i></span>
            </div>
            <div style="padding:14px 16px">
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:8px">
                <span style="font-weight:600;font-size:13px;color:#495057">{{ v.title }}</span>
                <span class="appui-badge" :class="v.status ? 'appui-badge--green' : 'appui-badge--muted'" role="button" @click="toggle(v)">{{ v.status ? 'Ativo' : 'Oculto' }}</span>
              </div>
              <div style="font-size:11.5px;color:#adb5bd;margin-bottom:12px;word-break:break-all">{{ v.url_video }}</div>
              <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                <span class="appui-grip" :class="{ 'is-disabled': isFiltering }" :title="isFiltering ? 'Limpe a busca e o filtro para reordenar' : 'Arraste para reordenar'">⠿</span>
                <button class="appui-tbtn" @click="openEdit(v)">Editar</button>
                <button class="appui-tbtn appui-tbtn--danger" @click="remove(v)">Excluir</button>
              </div>
            </div>
          </div>
        </draggable>

        <!-- Rodapé: mostrar X por vez + contador -->
        <div v-if="filteredList.length" class="appui-card-footer">
          <div class="appui-perpage">
            <span>Mostrar</span>
            <select class="appui-select appui-select--sm" v-model.number="perPage">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="0">Todos</option>
            </select>
            <span>por vez</span>
          </div>
          <span class="appui-count">Mostrando {{ displayList.length }} de {{ filteredList.length }}</span>
        </div>
      </section>
    </div>

    <b-modal v-model="showModal" :title="form.id ? 'Editar vídeo' : 'Novo vídeo'" hide-footer>
      <form @submit.prevent="save()">
        <div class="form-group"><label>Título *</label><input type="text" class="form-control" v-model="form.title" required></div>
        <div class="form-group"><label>URL do vídeo *</label><input type="text" class="form-control" v-model="form.url_video" placeholder="https://youtube.com/..." required></div>
        <div class="form-group"><label>URL da thumbnail</label><input type="text" class="form-control" v-model="form.url_image"></div>
        <div class="form-group"><label>Descrição</label><textarea class="form-control" rows="3" v-model="form.description"></textarea></div>
        <div class="text-right">
          <button type="button" class="btn btn-light mr-2" @click="showModal = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, title: '', url_video: '', url_image: '', description: '' })

export default {
  components: { draggable },
  name: 'app-videos',
  data () {
    return { list: [], showModal: false, saving: false, form: empty(), search: '', perPage: 50 }
  },
  mounted () { this.load() },
  computed: {
    isFiltering () { return !!this.search },
    filteredList () {
      if (!this.search) return this.list
      const q = this.search.toLowerCase()
      return this.list.filter(v => (v.title || '').toLowerCase().includes(q))
    },
    displayList: {
      get () { return this.filteredList.slice(0, this.perPage || this.filteredList.length) },
      set (val) {
        if (this.isFiltering) return
        this.list = [...val, ...this.list.slice(val.length)]
      }
    }
  },
  methods: {
    clearFilters () { this.search = '' },
    load () {
      const loader = this.$loading.show()
      services.listVideos().then(res => { this.list = res.data.data }).catch(() => errorMessage('Falha ao carregar vídeos.', 3000)).finally(() => loader.hide())
    },
    openCreate () { this.form = empty(); this.showModal = true },
    openEdit (v) { this.form = { ...v }; this.showModal = true },
    async save () {
      this.saving = true
      try {
        const payload = { title: this.form.title, url_video: this.form.url_video, url_image: this.form.url_image || null, description: this.form.description || null }
        if (this.form.id) await services.updateVideo(this.form.id, payload)
        else await services.createVideo(payload)
        this.showModal = false; successMessage('Vídeo salvo.', 2000); this.load()
      } catch (e) { errorMessage('Falha ao salvar vídeo.', 3000) } finally { this.saving = false }
    },
    async toggle (v) { try { await services.changeVideoStatus(v.id); v.status = v.status ? 0 : 1 } catch (e) { errorMessage('Falha ao alterar status.', 3000) } },
    async remove (v) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${v.title}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteVideo(v.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async onReorder () {
      try {
        await services.reorderVideos(this.list.map(it => ({ id: it.id })))
      } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    }
  }
}
</script>
